import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiCalendar, FiTrendingUp, FiX, FiEdit3, FiTrash2, FiPlay, FiActivity } = FiIcons;

const TeamDetails = ({ team, onClose, onEdit, onDelete, onStartAssessment }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Excellent': return 'green';
      case 'Good': return 'blue';
      case 'Fair': return 'yellow';
      case 'Needs Improvement': return 'red';
      default: return 'gray';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return FiTrendingUp;
      case 'down': return FiTrendingUp;
      default: return FiTrendingUp;
    }
  };

  if (!team) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{team.name}</h3>
              <button 
                onClick={onClose} 
                className="text-gray-400 hover:text-gray-500"
              >
                <SafeIcon icon={FiX} className="h-6 w-6" />
              </button>
            </div>
            
            {team.description && (
              <p className="text-gray-600 mb-6">{team.description}</p>
            )}
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <SafeIcon icon={FiUsers} className="h-5 w-5 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-gray-600">Members</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{team.members?.length || 0}</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <SafeIcon icon={FiTrendingUp} className="h-5 w-5 text-green-600 mr-2" />
                  <span className="text-sm font-medium text-gray-600">Score</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{team.overallScore || 0}%</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <SafeIcon icon={FiCalendar} className="h-5 w-5 text-purple-600 mr-2" />
                  <span className="text-sm font-medium text-gray-600">Last Assessment</span>
                </div>
                <p className="text-sm font-bold text-gray-900">
                  {team.lastAssessment ? new Date(team.lastAssessment).toLocaleDateString() : 'Not assessed'}
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <SafeIcon icon={getTrendIcon(team.trend)} className={`h-5 w-5 mr-2 ${team.trend === 'up' ? 'text-green-500' : team.trend === 'down' ? 'text-red-500 rotate-180' : 'text-gray-400'}`} />
                  <span className="text-sm font-medium text-gray-600">Status</span>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-${getStatusColor(team.status)}-100 text-${getStatusColor(team.status)}-800`}>
                  {team.status}
                </span>
              </div>
            </div>
            
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Team Members</h4>
              <div className="grid grid-cols-2 gap-2">
                {team.members?.map((member, index) => (
                  <div key={index} className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded">
                    <SafeIcon icon={FiUsers} className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-700">{member}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {team.lastAssessment && (
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Assessment History</h4>
                <p className="text-sm text-gray-600">
                  Last assessment completed on {new Date(team.lastAssessment).toLocaleDateString()}.
                  {team.overallScore > 0 && ` Overall team health score: ${team.overallScore}%.`}
                </p>
                
                {team.assessmentHistory && team.assessmentHistory.length > 1 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Previous Assessments:</p>
                    <div className="space-y-1">
                      {team.assessmentHistory.slice(-3).reverse().slice(1).map((assessment, index) => (
                        <div key={index} className="text-xs text-gray-600">
                          {new Date(assessment.date).toLocaleDateString()} - {assessment.overallScore}% score
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button 
              onClick={() => onStartAssessment(team)}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              <SafeIcon icon={FiActivity} className="h-4 w-4 mr-2" />
              Start Assessment
            </button>
            <button 
              onClick={() => onEdit(team)}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              <SafeIcon icon={FiEdit3} className="h-4 w-4 mr-2" />
              Edit Team
            </button>
            <button 
              onClick={() => onDelete(team.id)}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-red-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              <SafeIcon icon={FiTrash2} className="h-4 w-4 mr-2" />
              Delete Team
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamDetails;